/**
 * Vivport API Client
 * 
 * Type-safe API client with complete coverage of all backend endpoints.
 * All functions are fully typed with input validation and response types.
 * 
 * Usage:
 * ```typescript
 * import { api } from '@/api';
 * 
 * // Authentication
 * const result = await api.auth.login({ email, password });
 * 
 * // Content management
 * const content = await api.content.getContent(id);
 * 
 * // Tags
 * const tags = await api.tags.listTags();
 * 
 * // Admin operations
 * const members = await api.admin.listMembers();
 * ```
 */

// Export HTTP client
export { httpClient } from './http-client';

// Export all API modules
import * as admin from './admin-api';
import * as agents from './agents-api';
import * as auth from './auth-api';
import * as calendars from './calendar-api';
import * as contentAttachments from './content-attachment-api';
import * as content from './content-api';
import * as filters from './filter-api';
import * as hubs from './hub-api';
import * as processes from './processes-api';
import * as missionControl from './mission-control-api';
import * as memberExternalAccounts from './member-external-account-api';
import * as speak2me from './speak2me-api';
import * as tagGroups from './tag-group-api';
import * as tags from './tag-api';

/**
 * Main API object with all endpoints organized by category
 */
export const api = {
  /**
   * Authentication endpoints
   * - Core auth: login, logout, refresh, getUser
   * - MFA: email and passkey verification
   * - Passkey registration
   * - Password reset
   * - Invitations
   */
  auth,
  agents,
  processes,
  missionControl,
  memberExternalAccounts,
  speak2me,
  calendars,

  /**
   * Content management endpoints
   * - CRUD operations: list, get, create, update, delete
   * - Actions: regenerate, connectAnchor, etc.
   * - Bulk operations: bulk delete
   * - Tracking: read, star, follow
   */
  content,
  contentAttachments,

  /**
   * Saved filter endpoints
   * - CRUD operations for PRIVATE and HUB saved filters
   */
  filters,

  /**
   * Hub endpoints
   * - Create Corporate Hub
   */
  hubs,

  /**
   * Tag management endpoints
   * - List and get tags
   * - Assign/remove tags from content
   * - Get content by tag
   */
  tags,
  tagGroups,

  /**
   * Admin endpoints (require admin privileges)
   * - Member management
   * - Invitations
   * - Status updates
   */
  admin,
};

// Export individual modules for tree-shaking
export {
  admin,
  agents,
  auth,
  calendars,
  content,
  contentAttachments,
  filters,
  hubs,
  processes,
  missionControl,
  memberExternalAccounts,
  speak2me,
  tagGroups,
  tags,
};

// Export types (re-export from backend)
    export type {
    AccessLevel, AdminListMembersResult, Document, DeletionCommitInput,
    CalendarProjection,
    CalendarQueryInput,
    CalendarQueryResult,
    CalendarSource,
    DeletionCommitResult,

    // Content subtypes
    Anchor,
    Event,
    // Content types
    Content, ContentActionResult, ContentCreate, ContentListResult,
    // Common types
    ContentType, ContentUpdate, InviteAcceptInput,
    InviteAcceptResult, InviteCheckInput,
    InviteCheckResult, InviteMemberInput,
    InviteMemberResult,
    // Auth types
    LoginInput,
    LoginResult, LogoutInput,
    LogoutResult, MfaLoginResult, MfaPreferenceInput,
    MfaToggleInput, MfaVerifyEmailInput,
    MfaVerifyPasskeyInput, PasskeyRegisterVerifyInput,
    PasskeyRegisterVerifyResult, PasswordResetInput,
    PasswordResetResult, PasswordResetSendEmailInput, Photo,
    Filter, FilterCreateInput, FilterDefinition, FilterScope, FilterUpdateInput, Recording, RefreshTokenInput,
    RefreshTokenResult,
    // Hub types
    Hub, HubCreateRequest, HubCreateResult, SignupInput,
    // Member external account types
    GoogleMemberExternalAuthStartResult,
    MemberExternalAccountCalendarManualImportInput,
    MemberExternalAccountCalendarManualImportResult,
    MemberExternalAccountDisconnectResult,
    MemberExternalAccountMessageManualImportInput,
    MemberExternalAccountMessageManualImportResult,
    MemberExternalAccountListResult,
    MemberExternalAccountSafe,

    // Admin types
    RegisterMemberInput,
    RegisterMemberResult, ReinviteMembersInput,
    ReinviteMembersResult,
    // Tag types
    Tag, TagCreateInput, TagGroup, TagGroupCreateInput, TagGroupSelectionMode, TagGroupUpdateInput, TagScope, TagUpdateInput, ToggleActiveStatusInput,
    ToggleActiveStatusResult,
    // Tracking types
    Tracking, UpdateAdminStatusInput,
    UpdateAdminStatusResult, UserInfoResult
  } from '@vivport/backend/types';

export type { MessageResult } from '@vivport/backend/types/common';

